import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { AlertTriangle, Loader2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useDemoStatus } from '@/hooks/useDemoStatus';

/**
 * Phase 84.22 · Blocks real-statement imports while demo data is present.
 *
 * Usage: render this component near the top of any import page. It
 * queries demo status on mount; if `has_demo` is true it exposes an
 * `openBlockDialog()` function via the ref-forwarding pattern of a
 * headless hook — simpler here to render an always-mounted dialog that
 * opens itself when the parent asks.
 *
 * Simpler API: consumer calls `useImportBlock()` in its own file to get
 * a bool + a helper that returns true if the import should be blocked.
 * See `useImportBlock` below.
 */

export function useImportBlock() {
  const { status, refresh, clearDemo } = useDemoStatus();
  const [open, setOpen] = useState(false);
  const [clearing, setClearing] = useState(false);
  const navigate = useNavigate();

  const shouldBlock = !!status?.has_demo;

  /**
   * Call this from your import trigger (file input onChange, "Continue"
   * button etc.). If demo data is present, opens the block dialog and
   * returns true (meaning: DO NOT proceed with the import). Otherwise
   * returns false and the caller proceeds normally.
   */
  const checkBeforeImport = () => {
    if (shouldBlock) {
      setOpen(true);
      return true;
    }
    return false;
  };

  const handleClearAndContinue = async () => {
    setClearing(true);
    try {
      const res = await clearDemo();
      if (res?.ok) {
        toast.success('Demo data cleared. You can now import your own statement.');
        setOpen(false);
        // Give React a beat to reconcile before the caller retries.
        await refresh();
      } else {
        toast.error('Could not clear demo data. Please try again.');
      }
    } finally {
      setClearing(false);
    }
  };

  const dialog = (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogContent data-testid="import-blocked-dialog">
        <AlertDialogHeader>
          <div className="flex items-center gap-2 text-primary">
            <AlertTriangle className="h-5 w-5" />
            <AlertDialogTitle>Clear demo data first</AlertDialogTitle>
          </div>
          <AlertDialogDescription>
            You&rsquo;re currently viewing the <span className="font-medium text-foreground">Priya from Dubai</span> demo
            persona. To keep your dashboard clean we don&rsquo;t mix demo and real data. Clear the demo now, and you can
            import your own statement in the next step.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={clearing} data-testid="import-blocked-keep-demo">
            Keep demo for now
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleClearAndContinue}
            disabled={clearing}
            data-testid="import-blocked-clear-btn"
          >
            {clearing ? (
              <>
                <Loader2 className="h-4 w-4 mr-1 animate-spin" /> Clearing…
              </>
            ) : (
              'Clear demo & continue'
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return { shouldBlock, checkBeforeImport, dialog, navigate };
}
